/**
 * Inbound half of the voice state machine (FE-INB-2), extracted as a hook so
 * twilio.context.tsx stays readable. It co-owns the provider's refs/setters
 * through `deps` — deliberately, because an accepted inbound call must plug
 * into the SAME machinery the outbound path uses (callRef + finishCallRef +
 * per-call state), so mute/hold/hangup, audio processors, the indicator and
 * the popup all work unchanged.
 *
 * Responsibilities:
 *   - Receiver lifecycle: register only while logged in AND owning a line
 *     (GET /phone-number/me); tear down on logout/unmount; `refreshReceiver()`
 *     re-checks after Settings mutations.
 *   - Ring phase: parse custom params, guard collisions (already in a call →
 *     reject), surface `incomingCall` for the banner, dismiss on the caller's
 *     abandon / 20s dial timeout (`cancel`). Load every lead sharing the
 *     caller's number so the agent can answer as another one.
 *   - Accept: hand the Call to the shared state machine with an
 *     inbound-flavored finish (stop local tracks + recycle the receiver — the
 *     Device rebuild is what releases the mic, mirroring the outbound
 *     per-call-Device teardown).
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import type { Call, Device } from "@twilio/voice-sdk";
import * as Sentry from "@sentry/react";
import { useAuthStore } from "@/store/auth/auth.store";
import { useCallerIdStore } from "@/store/phone-numbers/caller-id.store";
import { getMyPhoneNumber } from "@/services/phone-numbers.service";
import { createVoiceReceiver, type VoiceReceiver } from "./twilio-receiver";
import { acceptInboundCall } from "./inbound-call-wiring";
import { leadMatchName, loadLeadMatches, syncInboundCallLead } from "./inbound-lead-matches";
import { useSocketContext } from "./socket.context/socket.context";
import type { CallEndReason, TwilioCallState } from "./twilio.context";
import type { TLeadPhoneMatch } from "@/models/leads.model";

const E164 = /^\+\d{7,15}$/;

export type IncomingCallInfo = {
  sessionName: string;
  /** Caller's number when it is a real E.164; null for withheld/anonymous. */
  fromE164: string | null;
  toE164: string | null;
  /** The lead the call is answered as: the backend's best match, or the agent's pick. */
  leadId: number | null;
  leadName: string | null;
  /** Lead's profile photo URL, when the backend includes it. */
  leadAvatar?: string | null;
  /** The lead the backend attached to the call row (the ring's `leadId` parameter). */
  routedLeadId: number | null;
  /** Every lead sharing the caller's number, best first; empty until loaded. */
  leadMatches: TLeadPhoneMatch[];
  /** All matches, including those past `leadMatches`' limit. */
  leadMatchTotal: number;
};

export type InboundVoiceDeps = {
  callRef: MutableRefObject<Call | null>;
  callInFlightRef: MutableRefObject<boolean>;
  finishCallRef: MutableRefObject<
    ((reason: CallEndReason, nextState: "ended" | "error", source: string) => boolean) | null
  >;
  cleanupSocketRef: MutableRefObject<(() => void) | null>;
  currentMutedRef: MutableRefObject<boolean>;
  networkTerminatedRef: MutableRefObject<boolean>;
  activeDeviceRef: MutableRefObject<Device | null>;
  /** Resets all per-call state and publishes the call's identity (provider-owned). */
  beginCall: (identity: {
    leadName: string | null;
    phoneNumber: string | null;
    leadId: number | null;
    sessionName: string | null;
  }) => void;
  setState: Dispatch<SetStateAction<TwilioCallState>>;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
  setIsMuted: Dispatch<SetStateAction<boolean>>;
  setIsReconnecting: Dispatch<SetStateAction<boolean>>;
  setCallStartedAt: Dispatch<SetStateAction<Date | null>>;
  setCallEndReason: Dispatch<SetStateAction<CallEndReason | null>>;
};

const parseIncoming = (call: Call): IncomingCallInfo => {
  const params = call.customParameters;
  const rawFrom = params.get("fromE164") ?? "";
  const rawLeadId = Number(params.get("leadId"));
  const leadId = Number.isFinite(rawLeadId) && rawLeadId > 0 ? rawLeadId : null;
  return {
    sessionName: params.get("sessionName") ?? "",
    fromE164: E164.test(rawFrom) ? rawFrom : null,
    toE164: params.get("toE164") ?? null,
    leadId,
    leadName: params.get("leadName") ?? null,
    routedLeadId: leadId,
    leadMatches: [],
    leadMatchTotal: leadId !== null ? 1 : 0,
  };
};

export function useInboundVoice(deps: InboundVoiceDeps) {
  const socket = useSocketContext();
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  // Re-read on every use so the callbacks below stay referentially stable
  // (the actions context promises identity stability) without stale closures.
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const receiverRef = useRef<VoiceReceiver | null>(null);
  const receiverDeviceRef = useRef<Device | null>(null);
  const incomingCallRef = useRef<Call | null>(null);
  const incomingInfoRef = useRef<IncomingCallInfo | null>(null);
  const ringHandlersRef = useRef<{ cancel: () => void; error: () => void } | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCallInfo | null>(null);

  /** Drop the pending ring (caller gone, agent declined, logout, outbound started). */
  const clearIncoming = useCallback((reject: boolean, source: string) => {
    const call = incomingCallRef.current;
    if (!call) return;
    incomingCallRef.current = null;
    incomingInfoRef.current = null;
    const handlers = ringHandlersRef.current;
    ringHandlersRef.current = null;
    if (handlers) {
      call.off("cancel", handlers.cancel);
      call.off("error", handlers.error);
    }
    setIncomingCall(null);
    depsRef.current.setState((s) => (s === "incoming" ? "idle" : s));
    if (reject) {
      try { call.reject(); } catch { /* noop */ }
    }
    Sentry.addBreadcrumb({
      category: "twilio.receiver",
      level: "info",
      message: `Incoming call cleared (${source})`,
    });
  }, []);

  /** Merge into the ringing call's info — a no-op once that ring is gone. */
  const patchIncoming = useCallback((call: Call, patch: Partial<IncomingCallInfo>) => {
    const info = incomingInfoRef.current;
    if (incomingCallRef.current !== call || !info) return;
    incomingInfoRef.current = { ...info, ...patch };
    setIncomingCall(incomingInfoRef.current);
  }, []);

  const handleIncoming = useCallback((call: Call, device: Device) => {
    // Collision guard: mid-outbound (Valkey race) or a second simultaneous
    // ring — reject so the caller hears the unavailable path, logged MISSED.
    if (depsRef.current.callInFlightRef.current || incomingCallRef.current) {
      try { call.reject(); } catch { /* noop */ }
      return;
    }
    incomingCallRef.current = call;
    incomingInfoRef.current = parseIncoming(call);
    receiverDeviceRef.current = device;
    // Ring-phase only: the caller abandoning (or the 20s dial timeout) fires
    // `cancel`. Replaced by the call-phase handlers on accept.
    const handlers = {
      cancel: () => clearIncoming(false, "caller-cancelled"),
      error: () => clearIncoming(false, "ring-error"),
    };
    ringHandlersRef.current = handlers;
    call.on("cancel", handlers.cancel);
    call.on("error", handlers.error);
    setIncomingCall(incomingInfoRef.current);
    // Only claim the state machine when it is free — a terminal screen
    // ("ended"/"error") keeps rendering; the banner keys off `incomingCall`.
    depsRef.current.setState((s) => (s === "idle" ? "incoming" : s));
    // The banner is already up; the matches fill in while it rings.
    void loadLeadMatches(incomingInfoRef.current).then((patch) => {
      if (patch) patchIncoming(call, patch);
    });
  }, [clearIncoming, patchIncoming]);

  /** Answer the ringing call as another lead that shares the caller's number. */
  const selectIncomingLead = useCallback((leadId: number) => {
    const call = incomingCallRef.current;
    const match = incomingInfoRef.current?.leadMatches.find((lead) => lead.id === leadId);
    if (!call || !match) return;
    patchIncoming(call, { leadId: match.id, leadName: leadMatchName(match) });
  }, [patchIncoming]);

  const acceptIncoming = useCallback(() => {
    const d = depsRef.current;
    const call = incomingCallRef.current;
    const info = incomingInfoRef.current;
    if (!call || !info || d.callInFlightRef.current) return;
    d.callInFlightRef.current = true;

    // The ring phase is over; its handlers would race the terminal path.
    const handlers = ringHandlersRef.current;
    ringHandlersRef.current = null;
    if (handlers) {
      call.off("cancel", handlers.cancel);
      call.off("error", handlers.error);
    }
    incomingCallRef.current = null;
    incomingInfoRef.current = null;
    setIncomingCall(null);

    syncInboundCallLead(info);
    acceptInboundCall({
      call,
      info,
      deps: d,
      socket,
      device: receiverDeviceRef.current,
      recycleReceiver: () => {
        receiverDeviceRef.current = null;
        receiverRef.current?.recycle();
      },
    });
  }, [socket]);

  const rejectIncoming = useCallback(() => {
    clearIncoming(true, "agent-declined");
  }, [clearIncoming]);

  /**
   * (Re)evaluate whether this browser should be receiving: logged in AND the
   * user owns a line (any live status — a PROVISIONING number flips ACTIVE
   * server-side with no front signal, and an early registration is harmless).
   */
  const syncReceiver = useCallback(async () => {
    try {
      const mine = await getMyPhoneNumber();
      if (!useAuthStore.getState().isLoggedIn) return; // logged out mid-flight
      // Keep the outbound caller-ID cache fresh too: this runs at login and
      // after every Settings phone-number mutation (refreshReceiver).
      useCallerIdStore.getState().setMine(mine);
      const hasLine = !!mine.number;
      if (hasLine && !receiverRef.current) {
        receiverRef.current = createVoiceReceiver({ onIncoming: handleIncoming });
      } else if (!hasLine && receiverRef.current) {
        receiverRef.current.destroy();
        receiverRef.current = null;
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { component: "InboundVoice", operation: "syncReceiver" },
      });
    }
  }, [handleIncoming]);

  const refreshReceiver = useCallback(() => {
    void syncReceiver();
  }, [syncReceiver]);

  useEffect(() => {
    if (isLoggedIn) {
      void syncReceiver();
      return;
    }
    receiverRef.current?.destroy();
    receiverRef.current = null;
    receiverDeviceRef.current = null;
    clearIncoming(false, "logout");
  }, [isLoggedIn, syncReceiver, clearIncoming]);

  // Provider unmount: stop receiving (the live-call teardown is the provider's).
  useEffect(() => {
    return () => {
      receiverRef.current?.destroy();
      receiverRef.current = null;
    };
  }, []);

  return { incomingCall, acceptIncoming, rejectIncoming, selectIncomingLead, refreshReceiver };
}
